"use client";

import {
  PropertyPickerCommand,
  type PropertyPickerOption,
  PropertyPicker as PropertyPickerPrimitive,
} from "@repo/ui/components/property-picker";
import type * as React from "react";
import { useFieldContext } from "@/shared/forms/form-context";

export type { PropertyPickerOption };

interface PropertyPickerCommandBaseProps {
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  icon?: React.ReactNode;
}

export interface PropertyPickerCommandProps<T> extends PropertyPickerCommandBaseProps {
  options?: PropertyPickerOption<T>[];
  getOptionKey?: (value: T) => string;
  isEmpty?: (value: T) => boolean;
  searchFn?: (query: string) => Promise<PropertyPickerOption<T>[]>;
  renderItem?: (option: PropertyPickerOption<T>, isSelected: boolean) => React.ReactNode;
  onCreateOption?: (value: string) => void;
}

export function PropertyPickerCommandField<T>({
  options,
  getOptionKey,
  isEmpty,
  searchFn,
  renderItem,
  label,
  placeholder = "Search...",
  disabled,
  className,
  icon,
  onCreateOption,
}: PropertyPickerCommandProps<T>) {
  const field = useFieldContext<T>();

  const current = options?.find((o) => o.value === field.state.value);
  const displayValue = current?.displayLabel ?? current?.label;
  const triggerIcon = icon ?? current?.icon;

  return (
    <PropertyPickerPrimitive<T>
      value={isEmpty?.(field.state.value) ? null : field.state.value}
      onChange={(val) => {
        field.handleChange(val);
        field.handleBlur();
      }}
      disabled={disabled}
      label={label}
      placeholder={placeholder}
      className={className}
      icon={triggerIcon}
      displayValue={displayValue}
    >
      <PropertyPickerCommand<T>
        options={options ?? []}
        getOptionKey={getOptionKey}
        searchFn={searchFn}
        renderItem={renderItem}
        placeholder={placeholder}
        onCreateOption={onCreateOption}
      />
    </PropertyPickerPrimitive>
  );
}
